import { Children, isValidElement, useCallback } from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableHead, TableRow } from '@mui/material';
import { Style } from '../Commons/Styles.jsx';

import {
    Datagrid as RaDatagrid,
    DatagridHeaderCell,
    useListContext,
} from 'react-admin';

/**
 * react-admin's own <DatagridHeader> doesn't let a field customize its header
 * cell style (only headerClassName), so this mirrors it just enough to keep
 * supporting the per-field `styleheader` prop the List pages already rely on.
 */
const CustomHeader = ({ children, hasExpand = false }) => {
    const { sort, setSort } = useListContext();

    const updateSort = useCallback(
        event => {
            if (!setSort) return;
            event.stopPropagation();
            const newField = event.currentTarget.dataset.field;
            const newOrder =
                sort?.field === newField
                    ? sort?.order === 'ASC'
                        ? 'DESC'
                        : 'ASC'
                    : event.currentTarget.dataset.order;
            setSort({ field: newField, order: newOrder });
        },
        [sort?.field, sort?.order, setSort]
    );

    return (
        <TableHead>
            <TableRow>
                {hasExpand && <TableCell padding="none" />}
                {Children.map(children, (field, index) =>
                    isValidElement(field) ? (
                        <DatagridHeaderCell
                            key={field.props.source || index}
                            field={field}
                            sort={sort}
                            isSorting={
                                sort?.field ===
                                (field.props.sortBy || field.props.source)
                            }
                            updateSort={updateSort}
                            style={field.props.styleheader ?? Style.DataGrid}
                        />
                    ) : null
                )}
            </TableRow>
        </TableHead>
    );
};

const Datagrid = ({ bulkActionButtons = false, size = 'medium', ...rest }) => (
    // react-admin's <Datagrid> por defecto usa size="small" (filas compactas,
    // menos padding en las celdas). Aca se cambia el default a "medium" para
    // que las filas tengan mas aire y no se vean tan pegadas a los datos;
    // cualquier lista puede seguir pasando size="small" explicitamente si
    // en algun caso puntual se prefiere compacto.
    <RaDatagrid
        header={CustomHeader}
        bulkActionButtons={bulkActionButtons}
        size={size}
        {...rest}
    />
);

Datagrid.propTypes = {
    body: PropTypes.element,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    expand: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
    hover: PropTypes.bool,
    rowClick: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    isRowSelectable: PropTypes.func,
};

export default Datagrid;
